/**
 * The arena map. One layer is the imported Map.obj, recentered so its floor top
 * sits at y=0 and the interior is centered on the origin. The game is an endless
 * vertical TOWER: identical layers stacked LAYER_HEIGHT apart, only three
 * rendered at once (the one below, the current one, the one above).
 *
 * Two holes punched through the floor:
 *   • SPAWN_HOLE    — enemy tanks (and, on the ground floor, the player) rise up
 *                     from here on a platform.
 *   • ELEVATOR_HOLE — clear the layer and an elevator car rises here; drive onto
 *                     it to be carried one layer up.
 *
 * The recenter transform applied to the asset is (+410, -40, -40): the asset's
 * floor top was at Y=40, interior centered at X=-410 Z=40. Collision is a small
 * hand-list of axis-aligned boxes (the whole map is axis-aligned), which is far
 * more robust than mesh raycasts for tank/shell physics.
 */

import * as THREE from "three";
import { OBJLoader } from "three/examples/jsm/loaders/OBJLoader.js";
import { MTLLoader } from "three/examples/jsm/loaders/MTLLoader.js";
import { log } from "../engine/debug";
import { Arena, CELL, type ArenaLayout } from "./arena";

const BASE = "/models/map/";
const RECENTER = new THREE.Vector3(410, -40, -40);

export const LAYER_HEIGHT = 180;

export interface AABB {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
  // WEDGE: a right-triangle prism (solid). `wedge` (0-3) = the corner the right angle sits in;
  // (nx,nz) = the hypotenuse's outward normal (toward the open corner). Tanks slide the slope; shells bank.
  wedge?: number;
  nx?: number;
  nz?: number;
  // WATER: a floor feature — blocks tank MOVEMENT (resolved out like a wall) but shells fly OVER it.
  water?: boolean;
}

// ---- base map geometry (the Map.obj asset, BEFORE per-layer scaling) --------
const BASE_HALF_X = 290, BASE_HALF_Z = 160; // asset interior half-extents (XZ)
// the two holes in the asset (centres + footprint). Scaled per-axis with the floor.
const BASE_SPAWN_HOLE = { minX: 210, maxX: 250, minZ: 60, maxZ: 100, cx: 230, cz: 80 };
const BASE_ELEV_HOLE = { minX: -250, maxX: -210, minZ: -100, maxZ: -60, cx: -230, cz: -80 };
// hole curbs: REMOVED entirely (both the elevator AND spawn holes). The entry/exit holes
// alternate per floor, so a leftover curb ring on one hole is a wall around the elevator on
// half the floors — the user wants both holes open on every side, all modes.
const BASE_CURBS: AABB[] = [];
// the full hole+curb footprint per hole (asset coords) — the maze must stay off
// this whole box (scaled), else blocks intersect the elevator/spawn walls.
const BASE_SPAWN_FOOTPRINT: AABB = { minX: 190, maxX: 250, minZ: 40, maxZ: 120 };
const BASE_ELEV_FOOTPRINT: AABB = { minX: -250, maxX: -190, minZ: -120, maxZ: -40 };

// ---- per-floor grid growth (QUANTITY, not tile size) -----------------------
// Every floor uses the SAME CELL; bigger floors get MORE cells. The interior is
// exactly cols·CELL × rows·CELL, the Map.obj floor is scaled per-axis to cover
// it, and the maze grid lines up with the walls exactly — no misalignment, no
// "caps". High layers become large, intricate mazes (not the same maze enlarged).
const BASE_COLS = 14, BASE_ROWS = 8; // layer-1 grid (≈ the original footprint)
const GRID_EVERY = 3; // layers per growth step
const GRID_MAX_STEPS = 43; // tops out ~100×50 cells at very high layers

export interface FullLayout extends ArenaLayout {
  fx: number; fz: number; halfX: number; halfZ: number; zoom: number;
}

/** The full layout for a layer: grid size, per-axis floor scale, world hole
 *  centres + footprints, and the camera pull-back. Pure function of `layer`. */
export function layerLayout(layer: number): FullLayout {
  // `layer` is the 0-based floor index (idx). Floor 0 is the base grid.
  const steps = Math.min(Math.max(0, Math.floor(layer / GRID_EVERY)), GRID_MAX_STEPS);
  const cols = BASE_COLS + 2 * steps;
  const rows = BASE_ROWS + steps;
  const halfX = (cols * CELL) / 2, halfZ = (rows * CELL) / 2;
  const fx = halfX / BASE_HALF_X, fz = halfZ / BASE_HALF_Z;
  const sc = (b: AABB): AABB => ({ minX: b.minX * fx, maxX: b.maxX * fx, minZ: b.minZ * fz, maxZ: b.maxZ * fz });
  return {
    cols, rows, halfX, halfZ, fx, fz,
    zoom: 1 + Math.min(steps, 12) * 0.03, // mild pull-back on bigger floors (awareness; tile size stays constant)
    spawn: { cx: BASE_SPAWN_HOLE.cx * fx, cz: BASE_SPAWN_HOLE.cz * fz },
    elev: { cx: BASE_ELEV_HOLE.cx * fx, cz: BASE_ELEV_HOLE.cz * fz },
    spawnHole: sc(BASE_SPAWN_HOLE),
    elevHole: sc(BASE_ELEV_HOLE),
    spawnClear: sc(BASE_SPAWN_FOOTPRINT),
    elevClear: sc(BASE_ELEV_FOOTPRINT),
  };
}

/** where the player faces / drives after entering each layer (toward arena center) */
export const SPAWN_EXIT = new THREE.Vector3(0, 0, 0);

// ---- the CURRENT layer's WORLD collision bounds ----------------------------
// One layer is interactive at a time; every physics helper below reads these,
// refreshed once per layer entry by setLayerBounds(). Everything is WORLD-native
// now — no base↔world conversion (that dual-coordinate split was the root of the
// old grid misalignment).
export const WALLS: AABB[] = []; // perimeter (0..3); hole curbs removed (both holes open)
export const INTERIOR: AABB = { minX: -BASE_HALF_X, maxX: BASE_HALF_X, minZ: -BASE_HALF_Z, maxZ: BASE_HALF_Z };
let spawnHoleW: AABB = { minX: BASE_SPAWN_HOLE.minX, maxX: BASE_SPAWN_HOLE.maxX, minZ: BASE_SPAWN_HOLE.minZ, maxZ: BASE_SPAWN_HOLE.maxZ };
let elevHoleW: AABB = { minX: BASE_ELEV_HOLE.minX, maxX: BASE_ELEV_HOLE.maxX, minZ: BASE_ELEV_HOLE.minZ, maxZ: BASE_ELEV_HOLE.maxZ };
const WALL_T = 10; // perimeter wall thickness

/** Recompute the world collision bounds (walls, interior, holes) for `layer`.
 *  Walls hug the grid exactly, so a forced-wall column seals wall-to-wall. */
export function setLayerBounds(layer: number): void {
  const L = layerLayout(layer);
  INTERIOR.minX = -L.halfX; INTERIOR.maxX = L.halfX; INTERIOR.minZ = -L.halfZ; INTERIOR.maxZ = L.halfZ;
  const hx = L.halfX, hz = L.halfZ, T = WALL_T;
  const perim: AABB[] = [
    { minX: -hx - T, maxX: -hx, minZ: -hz - T, maxZ: hz + T }, // west  (WALLS[0])
    { minX: hx, maxX: hx + T, minZ: -hz - T, maxZ: hz + T }, // east  (WALLS[1])
    { minX: -hx - T, maxX: hx + T, minZ: -hz - T, maxZ: -hz }, // north (WALLS[2])
    { minX: -hx - T, maxX: hx + T, minZ: hz, maxZ: hz + T }, // south (WALLS[3])
  ];
  WALLS.length = 0;
  WALLS.push(...perim, ...BASE_CURBS.map((b) => ({ minX: b.minX * L.fx, maxX: b.maxX * L.fx, minZ: b.minZ * L.fz, maxZ: b.maxZ * L.fz })));
  spawnHoleW = { minX: L.spawnHole.minX, maxX: L.spawnHole.maxX, minZ: L.spawnHole.minZ, maxZ: L.spawnHole.maxZ };
  elevHoleW = { minX: L.elevHole.minX, maxX: L.elevHole.maxX, minZ: L.elevHole.minZ, maxZ: L.elevHole.maxZ };
}

function inAABB(x: number, z: number, b: AABB): boolean {
  return x >= b.minX && x <= b.maxX && z >= b.minZ && z <= b.maxZ;
}

/** Per-floor procedural obstacle boxes — set to the current layer's maze. They
 *  participate in tank collision + shell ricochet alongside the fixed WALLS.
 *
 *  BROADPHASE: on big floors DYN_WALLS holds ~1600 boxes; iterating all of them in
 *  resolveCircle/reflectCircle/LOS — especially the sniper's per-step forward-sim —
 *  was the dominant combat CPU cost. We bucket the boxes into a uniform grid so a
 *  query only tests the few cells near a point/segment. Each maze box is CELL-sized
 *  and grid-aligned, so it lands in exactly one bucket → queries never see dupes. */
let DYN_WALLS: AABB[] = [];
const BP_CELL = 40; // bucket size (matches the maze cell)
let bpGrid = new Map<number, AABB[]>();
const bpKey = (c: number, r: number): number => (c + 512) * 1024 + (r + 512);
export function setDynamicWalls(w: AABB[]): void {
  DYN_WALLS = w;
  bpGrid = new Map();
  for (const b of w) {
    const c0 = Math.floor(b.minX / BP_CELL), c1 = Math.floor((b.maxX - 1e-3) / BP_CELL);
    const r0 = Math.floor(b.minZ / BP_CELL), r1 = Math.floor((b.maxZ - 1e-3) / BP_CELL);
    for (let c = c0; c <= c1; c++) for (let r = r0; r <= r1; r++) {
      const k = bpKey(c, r); let arr = bpGrid.get(k); if (!arr) { arr = []; bpGrid.set(k, arr); } arr.push(b);
    }
  }
}
export function dynamicWalls(): AABB[] { return DYN_WALLS; }

// reused scratch buffers so queries don't allocate per call
const _bpHits: AABB[] = [];
/** Maze boxes whose bucket overlaps the circle (x,z,r) — a small candidate set. */
function bpCircle(x: number, z: number, r: number): AABB[] {
  _bpHits.length = 0;
  const c0 = Math.floor((x - r) / BP_CELL), c1 = Math.floor((x + r) / BP_CELL);
  const r0 = Math.floor((z - r) / BP_CELL), r1 = Math.floor((z + r) / BP_CELL);
  for (let c = c0; c <= c1; c++) for (let rr = r0; rr <= r1; rr++) {
    const arr = bpGrid.get(bpKey(c, rr)); if (arr) for (let i = 0; i < arr.length; i++) _bpHits.push(arr[i]);
  }
  return _bpHits;
}
const _bpSeg: AABB[] = [];
/** Maze boxes in every bucket the segment a→b crosses (Amanatides-Woo DDA). */
export function dynWallsAlong(ax: number, az: number, bx: number, bz: number): AABB[] {
  _bpSeg.length = 0;
  let cx = Math.floor(ax / BP_CELL), cz = Math.floor(az / BP_CELL);
  const ecx = Math.floor(bx / BP_CELL), ecz = Math.floor(bz / BP_CELL);
  const dx = bx - ax, dz = bz - az;
  const stepX = dx > 0 ? 1 : dx < 0 ? -1 : 0, stepZ = dz > 0 ? 1 : dz < 0 ? -1 : 0;
  const tDeltaX = dx !== 0 ? Math.abs(BP_CELL / dx) : Infinity, tDeltaZ = dz !== 0 ? Math.abs(BP_CELL / dz) : Infinity;
  let tMaxX = dx !== 0 ? ((cx + (stepX > 0 ? 1 : 0)) * BP_CELL - ax) / dx : Infinity;
  let tMaxZ = dz !== 0 ? ((cz + (stepZ > 0 ? 1 : 0)) * BP_CELL - az) / dz : Infinity;
  for (let guard = 0; guard < 4096; guard++) {
    const arr = bpGrid.get(bpKey(cx, cz)); if (arr) for (let i = 0; i < arr.length; i++) _bpSeg.push(arr[i]);
    if (cx === ecx && cz === ecz) break;
    if (stepX !== 0 && stepZ !== 0 && Math.abs(tMaxX - tMaxZ) < 1e-9) {
      // exact grid-corner crossing — collect BOTH diagonal buckets so neither is missed
      const a2 = bpGrid.get(bpKey(cx + stepX, cz)); if (a2) for (let i = 0; i < a2.length; i++) _bpSeg.push(a2[i]);
      const a3 = bpGrid.get(bpKey(cx, cz + stepZ)); if (a3) for (let i = 0; i < a3.length; i++) _bpSeg.push(a3[i]);
      tMaxX += tDeltaX; tMaxZ += tDeltaZ; cx += stepX; cz += stepZ;
    } else if (tMaxX < tMaxZ) { tMaxX += tDeltaX; cx += stepX; } else { tMaxZ += tDeltaZ; cz += stepZ; }
  }
  return _bpSeg;
}

export function isOverSpawnHole(x: number, z: number): boolean {
  return inAABB(x, z, spawnHoleW);
}
export function isOverElevator(x: number, z: number): boolean {
  return inAABB(x, z, elevHoleW);
}
/** Is (x,z) over the spawn hole (isA) or the elevator hole? (x,z) is WORLD. */
export function isOverHole(x: number, z: number, isA: boolean): boolean {
  return inAABB(x, z, isA ? spawnHoleW : elevHoleW);
}

/**
 * Resolve a circle (tank) out of every wall. Mutates pos.x/pos.z. A couple of
 * passes handle corners cleanly.
 */
export function resolveCircle(pos: { x: number; z: number }, r: number, includeDyn = true, skipWater = false): void {
  for (let pass = 0; pass < 2; pass++) {
    for (const b of WALLS) resolveBox(pos, r, b, skipWater);
    if (includeDyn) { const hits = bpCircle(pos.x, pos.z, r); for (let i = 0; i < hits.length; i++) resolveBox(pos, r, hits[i], skipWater); }
  }
}

/**
 * Collide-and-slide: call RIGHT AFTER resolveCircle. `preX,preZ` is the position just
 * before that resolve; `desX,desZ` is the motion the mover intended this frame. If the
 * resolver had to push the circle back against its own intent — i.e. it drove into a wall
 * or a wedge's diagonal — redirect the blocked motion along the contact tangent and
 * re-resolve. Forward-only movers (enemies/mercs drive along bodyYaw, with no free
 * sideways component) otherwise PIN on angled faces: every frame they re-aim straight
 * into the slope and the resolver cancels it dead. Sliding lets them glide off instead.
 * The re-resolve keeps them outside every wall, so this can never tunnel through one.
 */
export function slideCircle(pos: { x: number; z: number }, r: number, preX: number, preZ: number, desX: number, desZ: number, includeDyn = true): void {
  const nx = pos.x - preX, nz = pos.z - preZ; // the resolver's push-out this frame (≈ outward surface normal)
  const nl2 = nx * nx + nz * nz;
  if (nl2 < 0.04) return; // negligible correction → wasn't blocked
  const inv = 1 / Math.sqrt(nl2), ix = nx * inv, iz = nz * inv;
  const into = desX * ix + desZ * iz; // intended motion vs the outward normal
  if (into >= 0) return; // intent wasn't into the surface → nothing to slide along
  pos.x += desX - into * ix; // keep only the tangential (along-wall) component of the intent
  pos.z += desZ - into * iz;
  resolveCircle(pos, r, includeDyn);
}

/**
 * Hard-clamp a tank to the playable interior rectangle. SAFETY NET against tanks
 * ending up stranded OUTSIDE the perimeter: resolveCircle pushes a circle out of a
 * wall via its NEAREST face, so a tank shoved to a wall's OUTER side (e.g. by
 * inter-tank separation crowding near the edge) gets ejected further out and never
 * recovers — it just roams out of bounds. The interior is convex, so clamping is
 * always correct and a no-op for any tank already inside. Call AFTER resolveCircle.
 */
export function clampInterior(pos: { x: number; z: number }, r: number): void {
  if (pos.x < INTERIOR.minX + r) pos.x = INTERIOR.minX + r;
  else if (pos.x > INTERIOR.maxX - r) pos.x = INTERIOR.maxX - r;
  if (pos.z < INTERIOR.minZ + r) pos.z = INTERIOR.minZ + r;
  else if (pos.z > INTERIOR.maxZ - r) pos.z = INTERIOR.maxZ - r;
}

/** Push a circle (pos,r) out of one box. Water blocks tanks (default) but is skipped for shells
 *  (skipWater); a wedge pushes the circle off its solid triangle (slides along the diagonal). */
function resolveBox(pos: { x: number; z: number }, r: number, b: AABB, skipWater = false): void {
  if (b.water && skipWater) return; // shells fly over water (homing missiles)
  if (b.wedge !== undefined) { resolveWedge(pos, r, b); return; }
  const cx = Math.max(b.minX, Math.min(pos.x, b.maxX));
  const cz = Math.max(b.minZ, Math.min(pos.z, b.maxZ));
  const dx = pos.x - cx, dz = pos.z - cz, d2 = dx * dx + dz * dz;
  if (d2 >= r * r) return;
  if (d2 > 1e-6) {
    const d = Math.sqrt(d2);
    pos.x = cx + (dx / d) * r;
    pos.z = cz + (dz / d) * r;
  } else {
    // center is inside the box — push out along the least-penetration axis
    const toL = pos.x - b.minX, toR = b.maxX - pos.x;
    const toN = pos.z - b.minZ, toS = b.maxZ - pos.z;
    const m = Math.min(toL, toR, toN, toS);
    if (m === toL) pos.x = b.minX - r;
    else if (m === toR) pos.x = b.maxX + r;
    else if (m === toN) pos.z = b.minZ - r;
    else pos.z = b.maxZ + r;
  }
}

/**
 * Reflect a moving circle (shell) off the walls. Mutates pos + vel. Returns true
 * if it bounced this step. Reflects on the axis of least penetration.
 */
export function reflectCircle(pos: { x: number; z: number }, vel: { x: number; z: number }, r: number, includeDyn = true): boolean {
  let bounced = false;
  for (const b of WALLS) if (reflectBox(pos, vel, r, b)) bounced = true;
  if (includeDyn) { const hits = bpCircle(pos.x, pos.z, r); for (let i = 0; i < hits.length; i++) if (reflectBox(pos, vel, r, hits[i])) bounced = true; }
  return bounced;
}

/** Reflect a moving circle off one box. Returns true if it bounced. */
function reflectBox(pos: { x: number; z: number }, vel: { x: number; z: number }, r: number, b: AABB): boolean {
  if (b.water) return false; // shells fly OVER water — never bounce off it
  if (b.wedge !== undefined) return reflectWedge(pos, vel, r, b);
  const cx = Math.max(b.minX, Math.min(pos.x, b.maxX));
  const cz = Math.max(b.minZ, Math.min(pos.z, b.maxZ));
  const dx = pos.x - cx, dz = pos.z - cz;
  if (dx * dx + dz * dz >= r * r) return false;
  // decide bounce axis: which side of the box is closest
  const overlapX = Math.min(pos.x - b.minX, b.maxX - pos.x);
  const overlapZ = Math.min(pos.z - b.minZ, b.maxZ - pos.z);
  const insideX = pos.x > b.minX && pos.x < b.maxX;
  const insideZ = pos.z > b.minZ && pos.z < b.maxZ;
  // if the shell center is within the box's X-span, it hit a horizontal (N/S)
  // face -> flip Z; otherwise it hit a vertical (E/W) face -> flip X
  let flipX: boolean;
  if (insideX && !insideZ) flipX = false;
  else if (insideZ && !insideX) flipX = true;
  else flipX = overlapX < overlapZ ? true : false;
  if (flipX) { vel.x = -vel.x; pos.x = vel.x > 0 ? b.maxX + r : b.minX - r; }
  else { vel.z = -vel.z; pos.z = vel.z > 0 ? b.maxZ + r : b.minZ - r; }
  return true;
}

// ---- WEDGE (right-triangle prism) collision ------------------------------
/** World vertices of a wedge's solid triangle: [ax,az, bx,bz, cx,cz] (a = right-angle corner). */
function wedgeVerts(b: AABB): number[] {
  const { minX, maxX, minZ, maxZ } = b;
  switch (b.wedge) {
    case 0: return [minX, minZ, maxX, minZ, minX, maxZ]; // solid corner −X,−Z
    case 1: return [maxX, minZ, minX, minZ, maxX, maxZ]; // +X,−Z
    case 2: return [maxX, maxZ, minX, maxZ, maxX, minZ]; // +X,+Z
    default: return [minX, maxZ, minX, minZ, maxX, maxZ]; // 3: −X,+Z
  }
}
const triSign = (x1: number, z1: number, x2: number, z2: number, x3: number, z3: number) => (x1 - x3) * (z2 - z3) - (x2 - x3) * (z1 - z3);

/** Push a circle off a wedge's solid triangle (slides along the diagonal + legs). */
function resolveWedge(pos: { x: number; z: number }, r: number, b: AABB): void {
  const v = wedgeVerts(b);
  let bx = 0, bz = 0, bd2 = Infinity;
  for (let e = 0; e < 3; e++) { // nearest point on the 3 triangle edges
    const x1 = v[e * 2], z1 = v[e * 2 + 1], x2 = v[((e + 1) % 3) * 2], z2 = v[((e + 1) % 3) * 2 + 1];
    const dx = x2 - x1, dz = z2 - z1, L2 = dx * dx + dz * dz || 1;
    let t = ((pos.x - x1) * dx + (pos.z - z1) * dz) / L2; if (t < 0) t = 0; else if (t > 1) t = 1;
    const cxp = x1 + t * dx, czp = z1 + t * dz, ex = pos.x - cxp, ez = pos.z - czp, d2 = ex * ex + ez * ez;
    if (d2 < bd2) { bd2 = d2; bx = cxp; bz = czp; }
  }
  const s1 = triSign(pos.x, pos.z, v[0], v[1], v[2], v[3]);
  const s2 = triSign(pos.x, pos.z, v[2], v[3], v[4], v[5]);
  const s3 = triSign(pos.x, pos.z, v[4], v[5], v[0], v[1]);
  const inside = !((s1 < 0 || s2 < 0 || s3 < 0) && (s1 > 0 || s2 > 0 || s3 > 0));
  const d = Math.sqrt(bd2) || 1e-4;
  if (inside) { pos.x = bx + (bx - pos.x) / d * r; pos.z = bz + (bz - pos.z) / d * r; } // eject to the nearest edge
  else if (bd2 < r * r) { pos.x = bx + (pos.x - bx) / d * r; pos.z = bz + (pos.z - bz) / d * r; } // graze
}

/** Reflect a shell off a wedge (a 45° bank off the diagonal, or off a leg). */
function reflectWedge(pos: { x: number; z: number }, vel: { x: number; z: number }, r: number, b: AABB): boolean {
  const v = wedgeVerts(b);
  let be = -1, bd2 = Infinity, bcx = 0, bcz = 0;
  for (let e = 0; e < 3; e++) {
    const x1 = v[e * 2], z1 = v[e * 2 + 1], x2 = v[((e + 1) % 3) * 2], z2 = v[((e + 1) % 3) * 2 + 1];
    const dx = x2 - x1, dz = z2 - z1, L2 = dx * dx + dz * dz || 1;
    let t = ((pos.x - x1) * dx + (pos.z - z1) * dz) / L2; if (t < 0) t = 0; else if (t > 1) t = 1;
    const cxp = x1 + t * dx, czp = z1 + t * dz, ex = pos.x - cxp, ez = pos.z - czp, d2 = ex * ex + ez * ez;
    if (d2 < bd2) { bd2 = d2; be = e; bcx = cxp; bcz = czp; }
  }
  if (bd2 >= r * r) return false;
  const x1 = v[be * 2], z1 = v[be * 2 + 1], x2 = v[((be + 1) % 3) * 2], z2 = v[((be + 1) % 3) * 2 + 1];
  const ox = v[((be + 2) % 3) * 2], oz = v[((be + 2) % 3) * 2 + 1]; // opposite vertex
  let nx = -(z2 - z1), nz = x2 - x1; const nl = Math.hypot(nx, nz) || 1; nx /= nl; nz /= nl;
  if (nx * ((x1 + x2) / 2 - ox) + nz * ((z1 + z2) / 2 - oz) < 0) { nx = -nx; nz = -nz; } // outward (away from interior)
  const vn = vel.x * nx + vel.z * nz;
  if (vn < 0) { vel.x -= 2 * vn * nx; vel.z -= 2 * vn * nz; pos.x = bcx + nx * r; pos.z = bcz + nz * r; return true; }
  return false;
}

/** Build the platform car that rises in a hole (spawn / elevator). Geometry is
 *  shared across all layers (the material stays per-platform so it can glow). */
const PLATFORM_GEOM = new THREE.BoxGeometry(38, 6, 38);
function buildPlatform(hole: { cx: number; cz: number }, color: number): THREE.Mesh {
  const m = new THREE.MeshLambertMaterial({ color });
  const mesh = new THREE.Mesh(PLATFORM_GEOM, m);
  mesh.position.set(hole.cx, -3, hole.cz); // top flush with floor (y=0)
  mesh.receiveShadow = true;
  return mesh;
}

export class GameMap {
  /** the recentered, texture-stripped template for one layer */
  private template: THREE.Group | null = null;
  private scene!: THREE.Scene;
  /** rendered layers, keyed by layer index */
  private layers = new Map<number, THREE.Group>();
  /** both hole platforms per rendered layer (a = SPAWN_HOLE, b = ELEVATOR_HOLE) */
  private platforms = new Map<number, { a: THREE.Mesh; b: THREE.Mesh }>();
  /** procedural obstacle layout per rendered layer */
  private arenas = new Map<number, Arena>();
  ready: Promise<void>;

  constructor() {
    this.ready = Promise.resolve();
  }

  async load(scene: THREE.Scene): Promise<void> {
    this.scene = scene;
    const mtlText = await fetch(BASE + "Map.mtl").then((r) => r.text());
    const mtl = new MTLLoader().parse(mtlText.replace(/^\s*map_\w+.*$/gim, ""), ""); // drop texture refs
    mtl.preload();
    const obj = await new OBJLoader().setMaterials(mtl).loadAsync(BASE + "Map.obj");
    obj.position.copy(RECENTER);
    obj.updateMatrixWorld(true);
    // bake the recenter into a flat group of meshes (so clones are cheap + shadows behave)
    const tmpl = new THREE.Group();
    const inFP = (cx: number, cz: number, fp: AABB) => cx >= fp.minX && cx <= fp.maxX && cz >= fp.minZ && cz <= fp.maxZ;
    obj.traverse((o) => {
      if (!(o instanceof THREE.Mesh)) return;
      const g = o.geometry.clone() as THREE.BufferGeometry;
      g.applyMatrix4(o.matrixWorld);
      if (!g.getAttribute("normal")) g.computeVertexNormals();
      // skip the hole curbs (BOTH the elevator AND spawn holes): small meshes whose XZ footprint
      // sits inside a hole's reserved box. Identified by position (not OBJ group name), so it
      // survives any re-export. The big floor/perimeter meshes are centred elsewhere.
      g.computeBoundingBox();
      const bb = g.boundingBox!;
      const cx = (bb.min.x + bb.max.x) / 2, cz = (bb.min.z + bb.max.z) / 2;
      const span = Math.max(bb.max.x - bb.min.x, bb.max.z - bb.min.z);
      if (span < 200 && (inFP(cx, cz, BASE_ELEV_FOOTPRINT) || inFP(cx, cz, BASE_SPAWN_FOOTPRINT))) return;
      const mesh = new THREE.Mesh(g, o.material);
      mesh.receiveShadow = true;
      mesh.castShadow = false;
      tmpl.add(mesh);
    });
    // The tall perimeter walls (Part1Mtl, d≈0.3) were rendered as faint glass.
    // Hide them entirely — cleaner open-arena look against the skybox. Collision is
    // unaffected (the perimeter lives in the WALLS AABBs, not this mesh).
    tmpl.traverse((o) => {
      if (!(o instanceof THREE.Mesh)) return;
      const mats = Array.isArray(o.material) ? o.material : [o.material];
      const glass = (mats as THREE.MeshPhongMaterial[]).some((m) => m && (m.transparent || (m.opacity ?? 1) < 0.99));
      if (glass) o.visible = false;
    });
    this.template = tmpl;
    log.info("map", `loaded; ${tmpl.children.length} meshes per layer`);
  }

  floorY(layer: number): number {
    return LAYER_HEIGHT * layer;
  }

  /** The floor's diffuse colour, sampled from the largest visible slab in the map
   *  asset — used to tint the field-guide portrait ground so tanks stand on
   *  map-coloured earth. Falls back to a tan if the asset isn't loaded yet. */
  groundColor(): number {
    let bestArea = -1, hex = 0x936b30; // tan fallback (the asset's floor Kd)
    this.template?.traverse((o) => {
      if (!(o instanceof THREE.Mesh) || !o.visible) return;
      o.geometry.computeBoundingBox();
      const bb = o.geometry.boundingBox;
      if (!bb) return;
      const area = (bb.max.x - bb.min.x) * (bb.max.z - bb.min.z);
      if (area <= bestArea) return;
      const m = (Array.isArray(o.material) ? o.material[0] : o.material) as THREE.MeshPhongMaterial;
      if (m?.color) { bestArea = area; hex = m.color.getHex(); }
    });
    return hex;
  }

  /**
   * Render the current layer plus the two BELOW it (the tower recedes downward
   * as you climb). The layer ABOVE is never drawn — its opaque floor would block
   * the top-down camera. Exactly three layers are visible, and the set shifts up
   * as the player ascends.
   */
  ensureLayers(center: number): void {
    if (!this.template) return;
    // current + 2 below is the usual window; `extraAbove` lets the elevator rise
    // render the layer it's climbing INTO during the transition.
    const want = new Set([center + this.extraAbove, center, center - 1, center - 2].filter((l) => l >= 0));
    // remove out-of-range layers
    for (const [l, grp] of this.layers) {
      if (!want.has(l)) {
        this.scene.remove(grp);
        this.layers.delete(l);
        const p = this.platforms.get(l);
        if (p) { (p.a.material as THREE.Material).dispose(); (p.b.material as THREE.Material).dispose(); }
        this.platforms.delete(l);
        this.arenas.get(l)?.dispose();
        this.arenas.delete(l);
      }
    }
    // add missing
    for (const l of want) {
      if (this.layers.has(l)) continue;
      const layout = layerLayout(l);
      // outer container is WORLD-native (only positioned by height). The Map.obj
      // floor + its baked holes/curbs are a VISUAL skin scaled per-axis to cover
      // the grid; the maze grid is world-native and added UNSCALED.
      const grp = new THREE.Group();
      grp.position.y = this.floorY(l);
      const floor = this.template.clone(true);
      floor.scale.set(layout.fx, 1, layout.fz); // stretch the asset floor to the grid (heights stay)
      const a = buildPlatform(BASE_SPAWN_HOLE, 0x6b7176);
      const b = buildPlatform(BASE_ELEV_HOLE, 0x6b7176);
      floor.add(a, b); // platforms ride the scaled floor → land flush in the scaled holes
      grp.add(floor);
      const arena = new Arena(l, layout); // world-native procedural maze (constant CELL, grows in quantity)
      grp.add(arena.group);
      this.scene.add(grp);
      this.layers.set(l, grp);
      this.platforms.set(l, { a, b });
      this.arenas.set(l, arena);
    }
    // PERF: only the player's current floor renders its maze (and casts shadows).
    // Lower floors keep their floor slab for tower depth but drop the now-large
    // maze geometry — the biggest render + shadow-pass win on big floors.
    for (const [l, arena] of this.arenas) arena.group.visible = (l === center);
  }

  /** The procedural obstacle layout for a rendered layer (idx). */
  arenaFor(layerIdx: number): Arena | undefined {
    return this.arenas.get(layerIdx);
  }

  /** While >0, ensureLayers also renders this many layers ABOVE (for the rise). */
  extraAbove = 0;

  /** Glow (or un-glow) a hole's platform to signal it's the active elevator. */
  glowPlatform(layer: number, isA: boolean, active: boolean): void {
    const p = this.platforms.get(layer);
    if (!p) return;
    const m = (isA ? p.a : p.b).material as THREE.MeshLambertMaterial & { emissive?: THREE.Color };
    m.color.setHex(active ? 0x39d98a : 0x6b7176);
    m.emissive?.setHex(active ? 0x0d5a38 : 0x000000);
  }

  /** Lift a hole's platform `dy` studs above its resting position (elevator car). */
  liftPlatform(layer: number, isA: boolean, dy: number): void {
    const p = this.platforms.get(layer);
    if (!p) return;
    (isA ? p.a : p.b).position.y = -3 + dy; // -3 is the resting offset (top flush with floor)
  }

  /** Hide/show a hole's platform (while a free-standing elevator car covers it). */
  hidePlatform(layer: number, isA: boolean, hidden: boolean): void {
    const p = this.platforms.get(layer);
    if (!p) return;
    (isA ? p.a : p.b).visible = !hidden;
  }
}
